import { ReaderInterface } from '@capacitor-community/stripe-terminal';
import { UserDataType, ProductDataType } from '@am3nd-org/pointcraft-types';

import { chunkArray } from 'src/utils/chunk-array';

import { DeviceType } from 'src/store/SystemStore/types';

import { CompanionDeviceType } from 'src/types/companion';

import PlatformService from './PlatformDBService';
import { InventoryStats, SystemConfigType } from './types';

export type { InventoryStats };

// Prevents a Firestore sync from writing to the inventory table while a
// one-time chunk migration is still in progress.
let isMigrating = false;

export const deleteStripeTerminalConfig = async () => {
  const instance = await PlatformService.getInstance();
  await instance.deleteSystemConfig('connectedStripeTerminal');
};

export const getStripeTerminalConfigSaved = async () => {
  const instance = await PlatformService.getInstance();
  const reader = await instance.getSystemConfig<{
    readerData: ReaderInterface;
    readerId: string;
  }>('connectedStripeTerminal');
  return reader;
};

export const getAccountId = async () => {
  const instance = await PlatformService.getInstance();
  const account = await instance.getSystemConfig<{ accountId: string }>('businessAccountId');
  return account;
};

export const saveAccountId = async (accountId: string) => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig('businessAccountId', { accountId });
};

// ---------------------------------------------------------------------------
// Force logout control state — tracks which logout commands this device has
// already handled, preventing infinite logout loops after re-login.
// ---------------------------------------------------------------------------
const FORCE_LOGOUT_CONTROL_STATE_KEY = 'forceLogoutControlState';

export type ForceLogoutControlState = {
  businessId: string;
  deviceIdOrUserId: string;
  version: number;
  initializedAt: string; // ISO 8601
  shownAt?: string;
  handledAt?: string;
  status: 'initialized' | 'shown' | 'logged_out';
};

export const getForceLogoutControlState = async (): Promise<ForceLogoutControlState | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<ForceLogoutControlState>(
    FORCE_LOGOUT_CONTROL_STATE_KEY
  );
  return record?.data ?? null;
};

export const saveForceLogoutControlState = async (
  state: ForceLogoutControlState
): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(FORCE_LOGOUT_CONTROL_STATE_KEY, state);
};

export const clearForceLogoutControlState = async (): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.deleteSystemConfig(FORCE_LOGOUT_CONTROL_STATE_KEY);
};

export type CurrentDeviceTypeInDb = {
  id: string | null; // Allow null for companion devices without business location ID
  name: string;
  type: DeviceType;
  initialized: boolean;
  capacitorDeviceId: string | null;
  companionType?: CompanionDeviceType; // Track specific companion device type
};

export const setCurrentDeviceInDB = async (device: CurrentDeviceTypeInDb) => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig('currentDevice', device);
};

export const getCurrentDeviceFromDB =
  async (): Promise<SystemConfigType<CurrentDeviceTypeInDb> | null> => {
    const instance = await PlatformService.getInstance();
    const device = await instance.getSystemConfig<CurrentDeviceTypeInDb>('currentDevice');
    return device;
  };

export type CompanionDeviceTypeInDb = {
  deviceId: string;
  code: string;
  timestamp: number;
  companionType?: CompanionDeviceType; // The specific type of companion device
};
export const getCompanionDevice = async () => {
  const instance = await PlatformService.getInstance();
  const device = await instance.getSystemConfig<CompanionDeviceTypeInDb>('companionDevice');
  return device;
};

export const saveCompanionDevice = async (device: CompanionDeviceTypeInDb) => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig('companionDevice', device);
};

export const saveUsesBleReader = async (usesBleReader: boolean) => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig('usesBleReader', { usesBleReader });
};

export const getUsesBleReader = async () => {
  const instance = await PlatformService.getInstance();
  const usesBleReader = await instance.getSystemConfig<{ usesBleReader: boolean }>('usesBleReader');
  return usesBleReader;
};

export const clearLocalDataExceptAccountId = async () => {
  const instance = await PlatformService.getInstance();

  // Preserve both accountId and force logout control state before wiping
  const accountData = await instance.getSystemConfig<{ accountId: string }>('businessAccountId');
  const forceLogoutState = await getForceLogoutControlState();

  await Promise.all([
    instance.deleteAllSystemConfigs(),
    instance.clearAdminBusinesses(),
  ]);

  if (accountData?.data?.accountId) {
    await instance.updateOrCreateSystemConfig('businessAccountId', {
      accountId: accountData.data.accountId,
    });
  }

  // Re-persist force logout state as 'logged_out' so the next login can check the version
  // and avoid re-triggering the same logout command.
  if (forceLogoutState) {
    await saveForceLogoutControlState({
      ...forceLogoutState,
      status: 'logged_out',
      handledAt: forceLogoutState.handledAt ?? new Date().toISOString(),
    });
  }

  localStorage.clear();
};

// POS ACTIVE TAB
export type PosActiveTabInDb = {
  value: '0' | '1';
};
export const getPosActiveTab = async (): Promise<SystemConfigType<PosActiveTabInDb> | null> => {
  const instance = await PlatformService.getInstance();
  const tab = await instance.getSystemConfig<PosActiveTabInDb>('posActiveTab');
  return tab;
};

export const savePosActiveTab = async (tab: PosActiveTabInDb): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig('posActiveTab', tab);
};

// save chosen ble reader to db
export const saveChosenBleReaderToDB = async (reader: { serialNumber: string }): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig('chosenBleReader', reader);
};

// get chosen ble reader from db
export const getChosenBleReaderFromDB = async (): Promise<{ serialNumber: string } | null> => {
  const instance = await PlatformService.getInstance();
  const reader = await instance.getSystemConfig<{ serialNumber: string }>('chosenBleReader');
  return reader?.data || null;
};

// delete chosen ble reader from db
export const deleteChosenBleReaderFromDB = async (): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.deleteSystemConfig('chosenBleReader');
};

// ---------------------------------------------------------------------------
// Inventory sync metadata — persists the last-known remote version + timestamp
// ---------------------------------------------------------------------------
const INVENTORY_SYNC_META_KEY = 'inventory_sync_meta';

export type InventorySyncMeta = {
  version: number;
  lastSyncedAt: string; // ISO 8601
};

export const getInventorySyncMeta = async (): Promise<InventorySyncMeta | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<InventorySyncMeta>(INVENTORY_SYNC_META_KEY);
  return record?.data ?? null;
};

export const saveInventorySyncMeta = async (meta: InventorySyncMeta): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(INVENTORY_SYNC_META_KEY, meta);
};

// ---------------------------------------------------------------------------
// User data sync metadata — persists the last-seen Firestore versions for
// userProfile and businessProfile so we can skip unnecessary refetches.
// ---------------------------------------------------------------------------
const USER_DATA_SYNC_META_KEY = 'user_data_sync_meta';

export type UserDataSyncMeta = {
  userProfileVersion: number;
  businessProfileVersion: number;
  lastSyncedAt: string; // ISO 8601
};

export const getUserDataSyncMeta = async (): Promise<UserDataSyncMeta | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<UserDataSyncMeta>(USER_DATA_SYNC_META_KEY);
  return record?.data ?? null;
};

export const saveUserDataSyncMeta = async (meta: UserDataSyncMeta): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(USER_DATA_SYNC_META_KEY, meta);
};

// ---------------------------------------------------------------------------
// User data cache — full UserDataType blob so the store can be hydrated
// immediately on app boot without waiting for the preload network request.
// ---------------------------------------------------------------------------
const USER_DATA_CACHE_KEY = 'userData_cache';

export const getUserDataCache = async (): Promise<UserDataType | null> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<UserDataType>(USER_DATA_CACHE_KEY);
  return record?.data ?? null;
};

export const saveUserDataCache = async (data: UserDataType): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(USER_DATA_CACHE_KEY, data);
};

// ---------------------------------------------------------------------------
// Inventory cache — chunked to handle large product catalogs (6k+ records)
// ---------------------------------------------------------------------------
const INVENTORY_META_KEY = 'cachedInventory_meta';
const inventoryChunkKey = (i: number) => `cachedInventory_chunk_${i}`;
const INVENTORY_CHUNK_SIZE = 500;

export const saveInventoryToLocalDb = async (products: ProductDataType[]): Promise<void> => {
  const instance = await PlatformService.getInstance();
  const chunks = chunkArray(products, INVENTORY_CHUNK_SIZE);
  await Promise.all([
    instance.updateOrCreateSystemConfig(INVENTORY_META_KEY, { chunkCount: chunks.length }),
    ...chunks.map((chunk, i) => instance.updateOrCreateSystemConfig(inventoryChunkKey(i), chunk)),
  ]);
};

export const getInventoryFromLocalDb = async (): Promise<ProductDataType[] | null> => {
  const instance = await PlatformService.getInstance();
  const meta = await instance.getSystemConfig<{ chunkCount: number }>(INVENTORY_META_KEY);
  if (!meta?.data?.chunkCount) return null;

  const chunkResults = await Promise.all(
    Array.from({ length: meta.data.chunkCount }, (_, i) =>
      instance.getSystemConfig<ProductDataType[]>(inventoryChunkKey(i))
    )
  );

  // Treat missing chunks as a corrupt cache
  if (chunkResults.some((r) => !r?.data)) return null;
  return chunkResults.flatMap((r) => r!.data);
};

export const clearInventoryFromLocalDb = async (): Promise<void> => {
  const instance = await PlatformService.getInstance();
  const meta = await instance.getSystemConfig<{ chunkCount: number }>(INVENTORY_META_KEY);
  const chunkCount = meta?.data?.chunkCount ?? 0;
  await Promise.all([
    instance.deleteSystemConfig(INVENTORY_META_KEY),
    ...Array.from({ length: chunkCount }, (_, i) =>
      instance.deleteSystemConfig(inventoryChunkKey(i))
    ),
  ]);
};

// ---------------------------------------------------------------------------
// Held Sales Queue — stores held sales in the system_config table with 72h TTL
// ---------------------------------------------------------------------------
const HELD_SALES_QUEUE_KEY = 'held_sales_queue';

export type HeldSaleItem = {
  id: string;
  heldAt: string;    // ISO 8601
  expiresAt: string; // ISO 8601 — heldAt + 72h
  sale: any;         // SaleData — typed as any here to avoid circular dep with @am3nd-org/pointcraft-types
};

export const getHeldSalesQueueFromDB = async (): Promise<HeldSaleItem[]> => {
  const instance = await PlatformService.getInstance();
  const record = await instance.getSystemConfig<HeldSaleItem[]>(HELD_SALES_QUEUE_KEY);
  return record?.data ?? [];
};

export const saveHeldSalesQueueToDB = async (queue: HeldSaleItem[]): Promise<void> => {
  const instance = await PlatformService.getInstance();
  await instance.updateOrCreateSystemConfig(HELD_SALES_QUEUE_KEY, queue);
};

// ---------------------------------------------------------------------------
// InventoryLocalDB — helpers for the dedicated inventory table.
// These replace the chunked system_config approach for all new code paths.
// ---------------------------------------------------------------------------
const MIGRATION_SENTINEL_KEY = 'inventory_table_migrated_v1';

export namespace InventoryLocalDB {
  /** Upsert the full product list (used by full sync). */
  export async function saveAll(products: ProductDataType[]): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.bulkUpsertInventory(products);
    instance.recalculateInventoryStats().catch((err) =>
      console.error('[InventoryLocalDB] Stats recalculation failed after saveAll:', err)
    );
  }

  /** Upsert a subset of products (used by incremental sync). */
  export async function upsertMany(products: ProductDataType[]): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.bulkUpsertInventory(products);
    instance.recalculateInventoryStats().catch((err) =>
      console.error('[InventoryLocalDB] Stats recalculation failed after upsertMany:', err)
    );
  }

  /** Upsert a single product (used by Add/Edit dialogs for immediate consistency). */
  export async function upsertOne(product: ProductDataType): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.upsertInventoryItem(product);
    instance.recalculateInventoryStats().catch((err) =>
      console.error('[InventoryLocalDB] Stats recalculation failed after upsertOne:', err)
    );
  }

  /** Hard-delete by product ID (used by incremental sync deletions). */
  export async function hardDelete(productId: string): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.hardDeleteInventoryItem(productId);
    instance.recalculateInventoryStats().catch((err) =>
      console.error('[InventoryLocalDB] Stats recalculation failed after hardDelete:', err)
    );
  }

  /** Paginated read — primary data source for the Inventory V4 page and POS V3 products tab. */
  export async function getPage(params: {
    page: number;
    pageSize: number;
    search?: string;
    category?: string;
  }): Promise<{ items: ProductDataType[]; total: number }> {
    const instance = await PlatformService.getInstance();
    return instance.queryInventoryPage(params);
  }

  /** Count of active (non-deleted) products — for the stats row. */
  export async function count(): Promise<number> {
    const instance = await PlatformService.getInstance();
    return instance.countInventory();
  }

  /** Sum of stock levels across all active products — for the stats row. */
  export async function totalStock(): Promise<number> {
    const instance = await PlatformService.getInstance();
    return instance.sumInventoryStock();
  }

  /** Fetch a single active product by its primary ID. Returns null if not found or deleted. */
  export async function getById(productId: string): Promise<ProductDataType | null> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryById(productId);
  }

  /** Fetch a single active product by barcode/identifier. Returns null if not found or deleted. */
  export async function getByIdentifier(identifier: string): Promise<ProductDataType | null> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryByIdentifier(identifier);
  }

  /** Fetch a variable-price product by its short lookup code stored in physicalGood.variablePriceLookupCode. */
  export async function getByVariablePriceLookupCode(lookupCode: string): Promise<ProductDataType | null> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryByVariablePriceLookupCode(lookupCode);
  }

  /** Wipe all inventory rows (called on logout or after migration confirmation). */
  export async function clearAll(): Promise<void> {
    const instance = await PlatformService.getInstance();
    await instance.clearAllInventory();
    instance.recalculateInventoryStats().catch((err) =>
      console.error('[InventoryLocalDB] Stats recalculation failed after clearAll:', err)
    );
  }

  /** Returns cached inventory stats, recalculating from source if the cache is missing. */
  export async function getStats(options?: {
    recalculateIfMissing?: boolean;
    forceRecalculate?: boolean;
  }): Promise<InventoryStats> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryStats(options);
  }

  /** Returns current per-category product counts directly from the inventory table. */
  export async function getCategoryCounts(): Promise<Array<{ category: string; count: number }>> {
    const instance = await PlatformService.getInstance();
    return instance.getInventoryCategoryCounts();
  }

  /** Recalculates all inventory stats from source and persists the result to the cache table. */
  export async function recalculateStats(): Promise<InventoryStats> {
    const instance = await PlatformService.getInstance();
    return instance.recalculateInventoryStats();
  }

  /**
   * Whether a Firestore sync should be held off because migration is in flight.
   * The sync service checks this before writing to the inventory table.
   */
  export function isMigrationInProgress(): boolean {
    return isMigrating;
  }

  /**
   * One-time migration from old chunked system_config storage to the new inventory table.
   * Returns true if migration was performed, false if already done or no data to migrate.
   * Safe to call on every boot — guarded by a persistent sentinel key.
   */
  export async function migrateFromChunks(): Promise<boolean> {
    const instance = await PlatformService.getInstance();

    // Already migrated?
    const flag = await instance.getSystemConfig<{ done: boolean }>(MIGRATION_SENTINEL_KEY);
    if (flag?.data?.done === true) return false;

    isMigrating = true;
    try {
      // Ensure the inventory table exists before writing
      await instance.ensureInventoryTable();

      // Attempt to read old chunks
      const cached = await getInventoryFromLocalDb();
      if (!cached || cached.length === 0) {
        // No chunk data — first install or already cleared; mark done and exit
        await instance.updateOrCreateSystemConfig(MIGRATION_SENTINEL_KEY, { done: true });
        return false;
      }

      // Bulk-write to the new inventory table
      await instance.bulkUpsertInventory(cached);

      // Persist sentinel so subsequent boots skip this
      await instance.updateOrCreateSystemConfig(MIGRATION_SENTINEL_KEY, { done: true });

      console.log(
        `[InventoryLocalDB] Migrated ${cached.length} products from chunks to inventory table`
      );

      // NOTE: Old chunks are intentionally left intact here.
      // They will be cleaned up in Phase 6 once the new path is confirmed stable.

      return true;
    } finally {
      isMigrating = false;
    }
  }
}
